import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import CustomAlert from '../CustomAlert';
import { t } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

const InvoiceSimpleGenerator = forwardRef(({ existingInvoiceData = null, isEdit = false }, ref) => {
  const [invoice, setInvoice] = useState({
    invoiceNumber: '001',
    date: new Date().toISOString().slice(0, 10),
    partner_id: null,
    recipient: '',
    recipientAddress: '',
    recipientBankNo: '',
    items: [{ id: 1, name: '', quantity: 1, price: '', taxRate: 18 }],
    currency: 'ден',
    status: 'complete'
  });

  const [businessDetails, setBusinessDetails] = useState({
    full_name: '',
    currency: 'ден'
  });

  // Partner search state
  const [suggestionsPartner, setSuggestionsPartner] = useState([]);
  const [showPartnerSuggestions, setShowPartnerSuggestions] = useState(false);
  const [queryPartner, setQueryPartner] = useState('');

  // Product search state
  const [suggestionsProduct, setSuggestionsProduct] = useState([]);
  const [productActiveRow, setProductActiveRow] = useState(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Alert state
  const [alert, setAlert] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    details: ''
  });

  const showAlert = (type, title, message, details = '') => {
    setAlert({ isOpen: true, type, title, message, details });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, isOpen: false }));
  };

  // Fetch business details
  const fetchBusinessDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/business-settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBusinessDetails({
          full_name: data.full_name || '',
          currency: data.currency || 'ден'
        });
        setInvoice(prev => ({
          ...prev,
          currency: data.currency || 'ден'
        }));
      }
    } catch (error) {
      console.error('Failed to fetch business details:', error);
    }
  };

  // Fetch next invoice number
  const fetchNextInvoiceNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/simple-invoice-next-number`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(prev => ({
          ...prev,
          invoiceNumber: data.nextNumber
        }));
      }
    } catch (error) {
      console.error('Failed to fetch next invoice number:', error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (!isEdit) {
      fetchNextInvoiceNumber();
    }
    fetchBusinessDetails();
  }, [isEdit]);

  // Load existing invoice data for edit mode
  useEffect(() => {
    if (isEdit && existingInvoiceData) {
      const { invoice: invoiceData, items } = existingInvoiceData;

      const transformedItems = items.map((item, index) => ({
        id: index + 1,
        product_id: item.product_id,
        name: item.name || item.product_name,
        quantity: item.quantity,
        price: item.price,
        taxRate: item.tax_rate || 18
      }));

      setInvoice(prev => ({
        ...prev,
        invoiceNumber: invoiceData.invoice_number || prev.invoiceNumber,
        date: invoiceData.date ? invoiceData.date.split('T')[0] : prev.date,
        partner_id: invoiceData.partner_id,
        recipient: invoiceData.customer_name || '',
        recipientAddress: invoiceData.address || '',
        recipientBankNo: invoiceData.bank_number || '',
        items: transformedItems.length > 0 ? transformedItems : prev.items,
        status: invoiceData.status || 'complete'
      }));
    }
  }, [isEdit, existingInvoiceData]);

  // Partner search effect
  useEffect(() => {
    const fetchPartnersSuggestions = async () => {
      if (queryPartner.length < 1) {
        setSuggestionsPartner([]);
        return;
      }

      try {
        const res = await fetch(`${apiUrl}/partner-search?name=${encodeURIComponent(queryPartner)}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestionsPartner(data);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
    };

    const timeout = setTimeout(fetchPartnersSuggestions, 100);
    return () => clearTimeout(timeout);
  }, [queryPartner]);

  // Click outside to close product suggestions
  useEffect(() => {
    const handleClickOutsideProduct = () => {
      setSuggestionsProduct([]);
      setProductActiveRow(null);
    };
    document.addEventListener('click', handleClickOutsideProduct);
    return () => document.removeEventListener('click', handleClickOutsideProduct);
  }, []);

  // Process items for calculations
  const processedItems = invoice.items.map(item => {
    const quantity = parseFloat(item.quantity || 0);
    const price = parseFloat(item.price || 0);
    const taxRate = parseFloat(item.taxRate || 18);
    const lineTotal = quantity * price;
    const taxAmount = lineTotal * (taxRate / 100);
    return { ...item, lineTotal, taxAmount };
  });

  const subtotal = processedItems.reduce((sum, item) => sum + item.lineTotal, 0);
  const tax = processedItems.reduce((sum, item) => sum + item.taxAmount, 0);
  const total = subtotal + tax;

  // Item change handler
  const handleProductItemChange = (index, field, value) => {
    const updatedItems = [...invoice.items];
    if (field === 'quantity') {
      updatedItems[index][field] = parseInt(value) || 1;
    } else if (field === 'price') {
      updatedItems[index][field] = parseFloat(value);
    } else {
      updatedItems[index][field] = value;
    }
    setInvoice({ ...invoice, items: updatedItems });
  };

  // Partner selection
  const handleSelectPartner = (partner) => {
    setInvoice({
      ...invoice,
      partner_id: partner.id,
      recipient: partner.name,
      recipientAddress: partner.address,
      recipientBankNo: partner.bank_number,
    });
    setShowPartnerSuggestions(false);
  };

  // Product selection
  const handleSelectProductAtIndex = (idx, product) => {
    const updatedItems = [...invoice.items];
    updatedItems[idx] = {
      ...updatedItems[idx],
      product_id: product.id,
      name: product.name,
      price: product.price || 0,
      taxRate: product.tax || 18,
      quantity: updatedItems[idx].quantity || 1,
    };

    setInvoice(prev => ({ ...prev, items: updatedItems }));
    setSuggestionsProduct([]);
    setProductActiveRow(null);
  };

  // Product search
  const handleProductSearch = async (idx, value) => {
    setProductActiveRow(idx);
    handleProductItemChange(idx, 'name', value);

    try {
      const res = await fetch(`${apiUrl}/product-search?name=${encodeURIComponent(value)}`);
      if (!res.ok) throw new Error('Failed to fetch suggestions');

      const data = await res.json();
      setSuggestionsProduct(data);
      setProductActiveRow(idx);
    } catch (error) {
      console.error('Error fetching product suggestions:', error);
      setSuggestionsProduct([]);
    }
  };

  // Row management
  const handleAddRow = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { id: Date.now(), name: '', quantity: 1, price: '', taxRate: 18 }],
    }));
  };

  const handleRemoveRow = (index) => {
    const updatedItems = invoice.items.filter((_, i) => i !== index);
    setInvoice(prev => ({ ...prev, items: updatedItems }));
  };

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!invoice.invoiceNumber?.trim()) {
      errors.invoiceNumber = t('Бројот на фактурата е задолжителен');
    }

    if (!invoice.partner_id) {
      errors.recipient = t('Ве молиме изберете партнер од листата');
    }

    if (!invoice.date) {
      errors.date = t('Датумот на фактурата е задолжителен');
    }

    const hasEmptyRows = invoice.items.some(item => {
      const hasAnyData = item.name?.trim() || item.quantity || item.price;
      const isComplete = item.name?.trim() &&
                        item.quantity &&
                        item.price &&
                        parseFloat(item.quantity) > 0 &&
                        parseFloat(item.price) > 0;
      return hasAnyData && !isComplete;
    });

    const validItems = invoice.items.filter(item =>
      item.name?.trim() &&
      item.quantity &&
      item.price &&
      parseFloat(item.quantity) > 0 &&
      parseFloat(item.price) > 0
    );

    if (validItems.length === 0) {
      errors.items = t('Потребна е барем една валидна ставка со назив, количина и цена');
    } else if (hasEmptyRows) {
      errors.items = t('Ве молиме пополнете ги сите ставки или отстранете ги празните редови пред зачувување');
    }

    return errors;
  };

  // Save draft
  const handleSaveDraft = async () => {
    await handleSave('draft');
  };

  // Reset form
  const resetInvoiceForm = async () => {
    setInvoice({
      invoiceNumber: '001',
      date: new Date().toISOString().slice(0, 10),
      partner_id: null,
      recipient: '',
      recipientAddress: '',
      recipientBankNo: '',
      items: [{ id: Date.now(), name: '', quantity: 1, price: '', taxRate: 18 }],
      currency: businessDetails.currency || 'ден',
      status: 'complete'
    });

    setSuggestionsPartner([]);
    setShowPartnerSuggestions(false);
    setQueryPartner('');
    setSuggestionsProduct([]);
    setProductActiveRow(null);

    await fetchNextInvoiceNumber();
  };

  // Save handler
  const handleSave = async (saveAsStatus = 'complete') => {
    setValidationErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const errorMessages = Object.values(errors).join('\n• ');
      showAlert('error', t('Грешка при валидација'), t('Ве молиме поправете ги следниве грешки:'), `• ${errorMessages}`);
      return;
    }

    try {
      const validItems = invoice.items.filter(item =>
        item.name?.trim() &&
        item.quantity &&
        item.price &&
        parseFloat(item.quantity) > 0 &&
        parseFloat(item.price) > 0
      );

      const sanitizedItems = validItems.map(item => ({
        product_id: typeof item.product_id === 'number' ? item.product_id : null,
        name: String(item.name || '').trim(),
        quantity: Number(item.quantity) || 0,
        price: Number(item.price) || 0,
        tax_rate: Number(item.tax_rate || item.taxRate || 18)
      }));

      const invoiceData = {
        partner_id: Number(invoice.partner_id) || null,
        invoice_number: String(invoice.invoiceNumber || '').trim(),
        date: String(invoice.date || ''),
        status: String(saveAsStatus),
        items: sanitizedItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          tax_rate: item.tax_rate
        }))
      };

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error(t('Ве молиме најавете се за да зачувате фактури.'));
      }

      let requestBody;
      try {
        requestBody = JSON.stringify(invoiceData);
      } catch (stringifyError) {
        console.error('Error serializing invoice data:', stringifyError);
        throw new Error(t('Неуспешна подготовка на податоците за фактурата. Ве молиме проверете за невалидни податоци.'));
      }

      const url = isEdit && existingInvoiceData?.invoice?.id
        ? `${apiUrl}/simple-invoice/${existingInvoiceData.invoice.id}`
        : `${apiUrl}/simple-invoice-add`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: requestBody,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      const invoiceId = result.id || existingInvoiceData?.invoice?.id;

      const isDraft = saveAsStatus === 'draft';
      const successVars = { number: invoiceData.invoice_number };
      let successMessage;
      if (isEdit) {
        successMessage = isDraft
          ? t('Фактура #{number} е успешно ажурирана како нацрт!', successVars)
          : t('Фактура #{number} е успешно ажурирана како завршена!', successVars);
      } else {
        successMessage = isDraft
          ? t('Фактура #{number} е успешно зачувана како нацрт!', successVars)
          : t('Фактура #{number} е успешно зачувана како завршена!', successVars);
      }
      const detailVars = { id: invoiceId, total: result.total };

      showAlert('success', isEdit ? t('Фактурата е ажурирана!') : t('Фактурата е зачувана!'),
        successMessage,
        isEdit
          ? t('Системски ID: {id} • Вкупно: {total}', detailVars)
          : t('Системски ID: {id} • Вкупно: {total} • Формуларот е исчистен за следната фактура.', detailVars)
      );

      setValidationErrors({});

      if (!isEdit) {
        await resetInvoiceForm();
      }

    } catch (error) {
      console.error('Error saving invoice:', error);

      let userMessage;
      if (error.message.includes('Failed to fetch')) {
        userMessage = t('Неуспешно зачувување на фактурата. Ве молиме проверете ја интернет врската и дали серверот работи.');
      } else if (error.message.includes('Server error: 500')) {
        userMessage = t('Неуспешно зачувување на фактурата. Настана грешка на серверот. Ве молиме обидете се повторно или контактирајте поддршка.');
      } else if (error.message.includes('required')) {
        userMessage = t('Неуспешно зачувување на фактурата. Некои задолжителни полиња недостасуваат или се невалидни.');
      } else {
        userMessage = t('Неуспешно зачувување на фактурата. {error}', { error: error.message });
      }

      showAlert('error', t('Неуспешно зачувување'), userMessage, error.message);
    }
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    handleSave,
    handleSaveDraft,
    handleAddRow,
  }));

  return (
    <div className="flex-1 min-h-[600px] bg-white p-8 rounded shadow flex flex-col pt-4">
        {/* Company name header */}
        {/* <div className="text-center mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            {businessDetails.full_name || 'Business Name'}
          </h2>
        </div> */}

        {/* Header */}
        <div className="flex justify-between mb-6 mt-2">
          <div className="w-2/3 border border-gray-400 rounded p-2 space-y-1 relative">
            <input
              type="text"
              value={invoice.recipient}
              onChange={(e) => {
                setInvoice({ ...invoice, recipient: e.target.value });
                setQueryPartner(e.target.value);
                setShowPartnerSuggestions(true);
                if (validationErrors.recipient) {
                  setValidationErrors(prev => ({ ...prev, recipient: undefined }));
                }
              }}
              className={`w-full p-1 border rounded text-sm font-bold ${
                validationErrors.recipient ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="До: Примач"
            />
            {showPartnerSuggestions && suggestionsPartner.length > 0 && (
              <ul className="absolute left-0 right-0 top-10 mt-1 bg-blue-50 border border-gray-300 rounded shadow-md max-h-48 overflow-auto text-sm z-50">
                {suggestionsPartner.map((partner) => (
                  <li
                    key={partner.id}
                    onClick={() => handleSelectPartner(partner)}
                    className="p-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:border-l-4 hover:border-blue-500 hover:shadow-sm cursor-pointer transition-all duration-200 ease-in-out"
                  >
                    <div className="font-medium">{partner.name}</div>
                    {partner.address && (
                      <div className="text-xs text-gray-600">{partner.address}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <input
              type="text"
              value={invoice.recipientAddress}
              onChange={(e) => setInvoice({ ...invoice, recipientAddress: e.target.value })}
              className="w-full p-1 border border-gray-300 rounded text-sm"
              placeholder="Адреса"
            />
            <input
              type="text"
              value={invoice.recipientBankNo}
              onChange={(e) => setInvoice({ ...invoice, recipientBankNo: e.target.value })}
              className="w-full p-1 border border-gray-300 rounded text-sm"
              placeholder="Банка - Жиро сметка"
            />
          </div>

          <div className="flex flex-col items-end justify-center">
            <label className="text-xs text-gray-500 mb-1">Датум</label>
            <input
              type="date"
              value={invoice.date}
              onChange={(e) => setInvoice({ ...invoice, date: e.target.value })}
              className="text-sm text-gray-700 border border-gray-300 rounded px-2 py-1"
            />
          </div>
        </div>

        {/* Invoice number */}
        <div className="w-full flex justify-center items-center mt-4 mb-2">
          <div className="flex items-center space-x-1">
            <label htmlFor="invoiceNumber" className="text-lg text-gray-700 font-bold">
              Фактура #
            </label>
            <input
              type="text"
              id="invoiceNumber"
              name="invoiceNumber"
              className={`border px-2 py-1 rounded text-left w-24 font-bold -ml-0.5 ${
                validationErrors.invoiceNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Фактура"
              value={invoice.invoiceNumber}
              onChange={(e) => {
                setInvoice({ ...invoice, invoiceNumber: e.target.value });
                if (validationErrors.invoiceNumber) {
                  setValidationErrors(prev => ({ ...prev, invoiceNumber: undefined }));
                }
              }}
            />
          </div>
        </div>

        {/* Items table */}
        <table className="w-full mb-2 border-b-2 border border-gray-600 rounded">
          <thead>
            <tr className="text-left border-b text-xs">
              <th className="py-0.5 w-6 text-center">#</th>
              <th className="py-0.5 whitespace-nowrap">Назив на артикал</th>
              <th className="py-0.5 w-8 text-center">EM</th>
              <th className="py-0.5 w-16 text-center">Количина</th>
              <th className="py-0.5 w-24 text-center">Цена</th>
              <th className="py-0.5 w-24 text-center">Без ДДВ</th>
              <th className="py-0.5 w-12 text-center">ДДВ %</th>
              <th className="py-0.5 w-24 text-right">ДДВ износ</th>
              <th className="py-0.5 w-24 text-right">Вкупно</th>
              <th className="py-0.5 w-5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, idx) => {
              const isItemValid = item.name?.trim() &&
                                  item.quantity &&
                                  item.price &&
                                  parseFloat(item.quantity) > 0 &&
                                  parseFloat(item.price) > 0;

              const hasAnyData = item.name?.trim() || item.quantity || item.price;
              const isIncompleteRow = hasAnyData && !isItemValid;
              const showItemError = validationErrors.items && (isIncompleteRow || !isItemValid);

              return (
                <tr key={item.id} className="border border-gray-300">
                  <td className="p-1 pl-2 text-xs">{idx + 1}</td>
                  <td className="p-1 relative">
                    <textarea
                      value={item.name}
                      placeholder="Назив на производ"
                      onChange={(e) => {
                        handleProductSearch(idx, e.target.value);
                        if (validationErrors.items) {
                          setValidationErrors(prev => ({ ...prev, items: undefined }));
                        }
                      }}
                      onFocus={() => setProductActiveRow(idx)}
                      className={`w-full px-1 py-0.5 text-xs resize-none leading-tight border-0 whitespace-pre-wrap break-words ${
                        showItemError ? 'bg-red-50' : ''
                      }`}
                      rows={3}
                      style={{ minHeight: '48px', lineHeight: '1.2' }}
                    />
                    {productActiveRow === idx && suggestionsProduct.length > 0 && (
                      <ul className="absolute left-0 z-10 w-full bg-white border border-gray-300 text-xs max-h-40 overflow-y-auto shadow-md">
                        {suggestionsProduct.map((suggestion, sidx) => (
                          <li
                            key={sidx}
                            className="px-2 py-1 cursor-pointer hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:border-l-4 hover:border-blue-500 hover:shadow-sm transition-all duration-200 ease-in-out"
                            onClick={() => {
                              handleSelectProductAtIndex(idx, suggestion);
                              setSuggestionsProduct([]);
                              setProductActiveRow(null);
                            }}
                          >
                            <div className="font-medium">{suggestion.name}</div>
                            {suggestion.price && (
                              <div className="text-gray-600">{t('Цена: {price}', { price: suggestion.price })}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="text-center">
                    <input type="text" value="пар" readOnly className="w-full px-1 py-0.5 text-xs text-center" />
                  </td>
                  <td>
                    <input
                      type="number"
                      placeholder="Кол."
                      min="1"
                      step="1"
                      value={Math.floor(item.quantity)}
                      onChange={(e) => {
                        const intValue = Math.floor(Number(e.target.value));
                        handleProductItemChange(idx, 'quantity', intValue || 1);
                        if (validationErrors.items) {
                          setValidationErrors(prev => ({ ...prev, items: undefined }));
                        }
                      }}
                      className={`w-full px-1 py-0.5 text-center placeholder:text-xs ${
                        showItemError ? 'bg-red-50' : ''
                      }`}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      placeholder="Цена"
                      value={item.price}
                      onChange={(e) => {
                        handleProductItemChange(idx, 'price', e.target.value);
                        if (validationErrors.items) {
                          setValidationErrors(prev => ({ ...prev, items: undefined }));
                        }
                      }}
                      className={`w-full px-1 py-0.5 text-center placeholder:text-xs ${
                        showItemError ? 'bg-red-50' : ''
                      }`}
                    />
                  </td>
                  <td className="px-1 py-0.5 text-center">
                    {item.quantity && item.price ? (item.quantity * item.price).toFixed(2) : '-'}
                  </td>
                  <td>
                    <select
                      value={item.taxRate}
                      onChange={(e) => handleProductItemChange(idx, 'taxRate', e.target.value)}
                      className="w-full px-1 py-0.5 border border-gray-300 rounded text-center"
                    >
                      <option value="5">5%</option>
                      <option value="18">18%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  <td className="px-1 py-0.5 text-right">
                    {(() => {
                      const taxRate = parseFloat(item.taxRate || 18);
                      const quantity = parseFloat(item.quantity || 0);
                      const price = parseFloat(item.price || 0);
                      return quantity && price ?
                        (quantity * price * (taxRate / 100)).toFixed(2) : '-';
                    })()}
                  </td>
                  <td className="px-1 py-0.5 text-right">
                    {(() => {
                      const taxRate = parseFloat(item.taxRate || 18);
                      const quantity = parseFloat(item.quantity || 0);
                      const price = parseFloat(item.price || 0);
                      const sub = quantity * price;
                      const taxAmt = sub * (taxRate / 100);
                      return quantity && price ?
                        (sub + taxAmt).toFixed(2) : '-';
                    })()}
                  </td>
                  <td className="px-1 py-0.5">
                    <button
                      onClick={() => handleRemoveRow(idx)}
                      className="text-red-500 hover:text-red-700 text-sm p-0 m-0 w-4 h-4 leading-none"
                      title={t('Отстрани ред')}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Add row button */}
        <button
          onClick={handleAddRow}
          className="w-full py-2 mb-4 border-2 border-dashed border-gray-300 text-gray-500 text-sm rounded hover:border-gray-400 hover:text-gray-700 transition-colors"
        >
          {t('+ Додади ред')}
        </button>

        {/* Validation errors */}
        {Object.keys(validationErrors).filter(key => validationErrors[key]).length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <h4 className="text-sm font-medium text-red-800 mb-2">{t('Ве молиме поправете ги следниве грешки:')}</h4>
            <ul className="text-sm text-red-700 list-disc list-inside">
              {Object.entries(validationErrors)
                .filter(([, message]) => message)
                .map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
            </ul>
          </div>
        )}

        {/* Totals */}
        <div className="flex justify-end mt-4 text-sm">
          <div className="text-right space-y-1">
            <div className="flex justify-start space-x-4">
              <div>
                Без ДДВ: <span className="font-medium">{subtotal.toFixed(2)}</span>
              </div>
              <div>
                ДДВ: <span className="font-medium">{tax.toFixed(2)}</span>
              </div>
            </div>

            {/* Tax breakdown by rate */}
            <div className="text-xs flex space-x-4 justify-end mt-1 text-gray-600">
              {[0, 5, 18].map(rate => {
                const itemsWithRate = invoice.items.filter(item =>
                  parseFloat(item.taxRate || 18) === rate && item.quantity && item.price
                );
                if (itemsWithRate.length === 0) return null;

                const taxBase = itemsWithRate.reduce((sum, item) =>
                  sum + parseFloat(item.quantity) * parseFloat(item.price), 0
                );
                const taxAmount = itemsWithRate.reduce((sum, item) =>
                  sum + parseFloat(item.quantity) * parseFloat(item.price) * (parseFloat(item.taxRate || 18) / 100), 0
                );

                return (
                  <div key={rate} className="flex space-x-2">
                    <span>{rate}%:</span>
                    <span>база {taxBase.toFixed(2)},</span>
                    <span>ДДВ {taxAmount.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>

            <div className="text-lg font-bold mt-2">
              Вкупно: <span className="text-blue-700">{total.toFixed(2)}</span>{' '}
              <span className="text-xs">{invoice.currency}</span>
            </div>
          </div>
        </div>
      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        details={alert.details}
      />
    </div>
  );
});

InvoiceSimpleGenerator.displayName = 'InvoiceSimpleGenerator';

export default InvoiceSimpleGenerator;
